import {
  IcErrorColored,
  IcInfo,
  IcSuccessColored,
  IcWarningColored
} from '@jds/core-icons';

import { Icon } from '../components/jdsicon/generated';
import { NotificationProps } from '../components/jdsnudge/logic';
import { ToastProps } from '../components/jdstoast/interface';
import { publish } from './pubsub';

export type SemanticToastProps = Omit<NotificationProps, 'icon'>;

export interface NotificationServiceProps {
  type: string;
  notifications: NotificationProps[];
}

export class NotificationService {
  static eventName = 'notification';
  static args: NotificationServiceProps;
  constructor(args: NotificationServiceProps) {
    NotificationService.args = args;
  }
}

export class NotificationCloseService {
  static eventName = 'close-notifications';
  static args: {};
  constructor(args: {}) {
    NotificationCloseService.args = args;
  }
}

export class NotificationController {
  static nudges: NotificationProps[] = [];
  static toasts: NotificationProps[] = [];

  private static updateNotifications(type: 'nudge' | 'toast') {
    if (type === 'nudge') {
      publish(
        new NotificationService({
          type,
          notifications: [...NotificationController.nudges]
        })
      );
      return;
    }
    publish(
      new NotificationService({
        type,
        notifications: [...NotificationController.toasts]
      })
    );
  }

  private static createID(type: 'nudge' | 'toast'): string {
    const id = new Date().getTime() + Math.random().toString();
    return this.getNotificationIndex(type, id) < 0 ? id : this.createID(type);
  }

  private static getNotificationIndex(
    type: 'nudge' | 'toast',
    id?: string
  ): number {
    if (type === 'nudge')
      return NotificationController.nudges.findIndex((item) => item.id === id);
    return NotificationController.toasts.findIndex((item) => item.id === id);
  }

  static getNotification(
    id: string,
    type: 'nudge' | 'toast'
  ): NotificationProps | void {
    const index = NotificationController.getNotificationIndex(type, id);
    if (this.isActive(id, type) && index >= 0) {
      if (type === 'nudge') return NotificationController.nudges[index];
      return NotificationController.toasts[index];
    }
  }

  static nudge(props: NotificationProps): string {
    if (!props.title) {
      console.warn(
        'Notification: [Nudge] cannot be created as title was not provided'
      );
      return '';
    }
    const index = this.getNotificationIndex('nudge', props.id);
    let ID = props.id;

    if (index < 0) {
      ID = props.id || this.createID('nudge');
      if (!NotificationController.isActive(ID, 'nudge')) {
        const nudgeItem = { showClose: true, ...props, id: ID };
        NotificationController.nudges.push(nudgeItem);
        this.updateNotifications('nudge');
      }
      return ID;
    }
    return ID as string;
  }

  static toast(props: ToastProps): string {
    if (!props.title) {
      console.warn(
        'Notification: [Toast] cannot be created as title was not provided'
      );
      return '';
    }
    const index = this.getNotificationIndex('toast', props.id);
    let ID = props.id;
    if (index < 0) {
      ID = props.id || this.createID('toast');
      if (!NotificationController.isActive(ID, 'toast')) {
        const toastItem = { showClose: true, ...props, id: ID };
        NotificationController.toasts.push(toastItem);
        this.updateNotifications('toast');
      }
      return ID;
    }
    return ID as string;
  }

  static dismiss(id: string, type: 'nudge' | 'toast'): string {
    if (this.isActive(id, type)) {
      publish(new NotificationCloseService({ [id]: type }));
      setTimeout(() => {
        if (type === 'nudge') {
          const newNudgeList = NotificationController.nudges.filter(
            (item) => item.id !== id
          );
          NotificationController.nudges = newNudgeList;
        } else {
          const newToastList = NotificationController.toasts.filter(
            (item) => item.id !== id
          );
          NotificationController.toasts = newToastList;
        }
        this.updateNotifications(type);
      }, 500);
    }
    return id;
  }

  static update(
    id: string,
    type: 'nudge' | 'toast',
    props: NotificationProps
  ): void {
    const notificationIndex = this.getNotificationIndex(type, id);
    if (notificationIndex >= 0) {
      if (this.isActive(id, type)) {
        if (type === 'nudge') {
          const currentNudge = NotificationController.nudges.findIndex(
            (nudge) => nudge.id === id
          );
          const updatedNudgeItem = {
            ...NotificationController.nudges[currentNudge],
            ...props
          };
          NotificationController.nudges[currentNudge] = updatedNudgeItem;
        } else {
          const currentToast = NotificationController.toasts.findIndex(
            (toast) => toast.id === id
          );
          const updatedToastItem = {
            ...NotificationController.toasts[currentToast],
            ...props
          };
          NotificationController.toasts[currentToast] = updatedToastItem;
        }
        this.updateNotifications(type);
      }
    }
  }

  static isActive(id: string, type: 'toast' | 'nudge'): boolean {
    const exists =
      type === 'nudge'
        ? NotificationController.nudges.findIndex(
            (nudgeItem) => nudgeItem.id === id
          ) >= 0
        : NotificationController.toasts.findIndex(
            (toastItem) => toastItem.id === id
          ) >= 0;
    return exists;
  }

  static dismissAll(type: 'nudge' | 'toast'): void {
    const activeIds = {};
    NotificationController.nudges.forEach((noti) => {
      if (noti.id) activeIds[noti.id] = 'nudge';
    });

    NotificationController.toasts.forEach((noti) => {
      if (noti.id) activeIds[noti.id] = 'toast';
    });
    publish(new NotificationCloseService(activeIds));
    setTimeout(() => {
      if (type === 'nudge') {
        NotificationController.nudges = [];
      } else {
        NotificationController.toasts = [];
      }
      this.updateNotifications(type);
    }, 500);
  }
}

const createProps = (props: NotificationProps | string) =>
  typeof props === 'string' ? { title: props } : props;

/**
 * Create a nudge notification
 * @param props The notification props
 * @returns `ID` The ID of the notification
 */
export const nudge = (props: NotificationProps | string): string =>
  NotificationController.nudge(createProps(props));

/**
 * Updates a nudge notification
 * @param id The ID of the nudge
 * @param props Update props
 */
nudge.update = (id: string, props: NotificationProps | string) =>
  NotificationController.update(id, 'nudge', createProps(props));

/**
 * Dismiss a nudge Notification
 * @param id  The ID of the nudge
 */
nudge.dismiss = (id: string) => NotificationController.dismiss(id, 'nudge');

/**
 * Dismiss all nudges
 */
nudge.dismissAll = () => NotificationController.dismissAll('nudge');

/**
 * Check whether a nudge is active
 * @param id The ID of the nudge
 * @returns {Boolean} whether a nudge is active
 */
nudge.isActive = (id: string): boolean =>
  NotificationController.isActive(id, 'nudge');

/**
 * Gets the nudge corresponding to the ID
 * @param id The ID of the nudge
 * @returns {NotificationProps} The corresponding nudge
 */
nudge.get = (id: string): NotificationProps | void =>
  NotificationController.getNotification(id, 'nudge');

/**
 * Create a toast notification
 * @param props The notification props. You can also pass a string directly which will serve as title
 * @returns `ID` The ID of the notification
 */
export const toast = (props: ToastProps | string): string =>
  NotificationController.toast(createProps(props));

/**
 * Updates a toast notification
 * @param id The ID of the toast
 * @param props Update props
 */
toast.update = (id: string, props: ToastProps | string) =>
  NotificationController.update(id, 'toast', createProps(props));

/**
 * Dismiss all toasts
 */
toast.dismissAll = () => NotificationController.dismissAll('toast');

/**
 * Dismiss a toast Notification
 * @param id  The ID of the toast
 */
toast.dismiss = (id: string) => NotificationController.dismiss(id, 'toast');

/**
 * Create an `info` toast notification with the `IcInfo` Icon
 * @param props The notification props. You can also pass a string directly which will serve as title.
 * @returns `ID` The ID of the toast
 */
toast.info = (props: SemanticToastProps | string) =>
  NotificationController.toast({
    ...createProps(props),
    icon: <Icon ic={<IcInfo />} size='l' />
  });

/**
 * Create an `error` toast notification with the `IcErrorColored` Icon
 * @param props The notification props. You can also pass a string directly which will serve as title
 * @returns `ID` The ID of the toast
 */
toast.error = (props: SemanticToastProps | string) =>
  NotificationController.toast({
    ...createProps(props),
    icon: <Icon ic={<IcErrorColored />} size='l' />
  });

/**
 * Create an `success` toast notification with the `IcSuccessColored` Icon
 * @param props The notification props. You can also pass a string directly which will serve as title
 * @returns `ID` The ID of the toast
 */
toast.success = (props: SemanticToastProps | string) =>
  NotificationController.toast({
    ...createProps(props),
    icon: <Icon ic={<IcSuccessColored />} size='l' />
  });

/**
 * Create an `warning` toast notification with the `IcWarningColored` Icon
 * @param props The notification props. You can also pass a string directly which will serve as title
 * @returns `ID` The ID of the toast
 */
toast.warning = (props: SemanticToastProps | string) =>
  NotificationController.toast({
    ...createProps(props),
    icon: <Icon ic={<IcWarningColored />} size='l' />
  });

/**
 * Check whether a toast is active
 * @param id The ID of the toast
 * @returns {Boolean} whether a toast is active
 */
toast.isActive = (id: string): boolean =>
  NotificationController.isActive(id, 'toast');

/**
 * Gets the nudge corresponding to the ID
 * @param id The ID of the nudge
 * @returns {NotificationProps} The corresponding nudge
 */
toast.get = (id: string): ToastProps | void =>
  NotificationController.getNotification(id, 'toast');
